import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { wikiActions } from "../../../redux/wiki";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";

const ExemploUseEffect = () => {
  const { pessoa } = useSelector((state) => state.wiki);
  const [counterObj] = useState({ count: 0 });
  const [searchArgument, setSearchArgument] = useState("");
  const [pageSize, setPageSize] = useState("50");
  const dispatch = useDispatch();

  const { request, data, isLoading } = useApiClient();

  // Quando possível, utilize os argumentos do Callback para passar valores.
  // Desse modo, se enxuga o array de dependências para evitar renderizações desnecessárias.
  const handleSearch = useCallback(() => {
    request(apiRoutes.fiscalizacao.parametrizacao.consultaFase, {
      searchArgument,
      pageSize,
      pageNumber: 1,
    });
  }, [request, searchArgument, pageSize]);

  // Esse exemplo utiliza o hook useRef() para chamar um callback no mount somente.
  const handleSearchRef = useRef(handleSearch);

  useEffect(() => {
    console.warn("Código executado no mount do componente");
    handleSearchRef.current();
  }, []);

  useEffect(() => {
    console.warn("Código executado a cada renderização");
  });

  // Na prática, estados transientes são gerenciados pelo Formik. Exemplo apenas.
  const inputChangeHandler = (e) => {
    dispatch(
      wikiActions.salvarEstado({
        ...pessoa,
        [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
      }),
    );
  };

  // useEffect(() => {
  //   DEVE SER EVITADO - /!\ COMPARAÇÃO DE REFERÊNCIA EM MEMÓRIA
  // }, [counterObj]);

  useEffect(() => {
    // O React compara valores ou referências (em caso de objetos)
  }, [counterObj.count]);

  useEffect(() => {
    const interval = setInterval(() => console.warn("Tick!"), 2000);
    return () => {
      console.warn("Clean up code (cada unmount ou destroy)");
      clearInterval(interval);
    };
  }, []);

  return (
    <div style={{ backgroundColor: "white", padding: "20px" }}>
      <h1>Exemplos de uso do hook React useEffect()</h1>
      <a href="https://reactjs.org/docs/hooks-effect.html" target="_blank" rel="noreferrer">
        Documentação React
      </a>
      <br />
      <a href="https://blog.logrocket.com/guide-to-react-useeffect-hook/" target="_blank" rel="noreferrer">
        Artigo Referência
      </a>
      <br />
      <h3>Dicas importantes:</h3>
      <li>O hook useEffect() foi pensado para lidar com mudanças de estado assíncronas;</li>
      <li>O código executado não bloqueia a UI;</li>
      <li>
        Uma dependência aparentemente está causando o que você acredita ser renderização &ldquo;desnecessária&ldquo; ou
        loop infinito? Não brigue com a lógica e simplesmente remova a declaração da dependência, revise a lógica do
        componente - use useCallback() ou useRef() para sanar o problema;
      </li>
      <li>Cuidado com referência de funções no corpo do componente. Elas mudam a cada ciclo de renderização;</li>
      <li>
        Use o seguinte mindset: declarar o mínimo de dependências possível, não o máximo. Se for o caso, separe a lógica
        em várias declarações do hook (SoC - Separation of Concern);
      </li>
      <li>
        Array de dependências vazio é diferente de não declarado: [(vazio)] Execução única após o mount do componente;
        undefined (não declarado) Execução após cada renderização;
        <br />
        <b>IMPORTANTE:</b> Como nesse exemplo, utilize useCallback() ou useRef() para implementar uma solução que
        execute uma única vez no mount do componente ao invés de um array vazio de dependências.&nbsp;
        <b>eslint-disable-next-line</b> é uma violação do padrão de projeto.&nbsp;
        <a href="https://github.com/facebook/react/issues/21750#issuecomment-868965080">Entenda.</a>
      </li>
      <li>É possível executar código de cleanup no unmount retornando uma função;</li>
      <li>
        O React garante que os setters retornados pelo hook useState() e de forma geral são a mesma referência na
        memória entre ciclos de renderização;
      </li>
      <br />
      <h4>useEffect() + Redux:</h4>
      <p>
        Nome:
        <br />
        <input type="text" name="nome" value={pessoa.nome} onChange={inputChangeHandler} />
      </p>
      <p>
        E-mail:
        <br />
        <input type="text" name="email" value={pessoa.email} onChange={inputChangeHandler} />
      </p>
      <p>
        Ativo:
        <br />
        <input type="checkbox" name="ativo" checked={pessoa.ativo} onChange={inputChangeHandler} />
      </p>
      <hr />
      <h4>useEffect() + useCallback() + useRef():</h4>
      <p>
        Argumento de Pesquisa:
        <br />
        <input type="text" name="arg" value={searchArgument} onChange={(e) => setSearchArgument(e.target.value)} />
        <br />
        <button type="button" disabled={isLoading} onClick={handleSearch}>
          Pesquisar
        </button>
        <br />
        Tam. Página:
        <br />
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(e.target.value);
            handleSearch();
          }}
        >
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </p>
      <br />
      <h4>Itens:</h4>
      <TextSpinner show={isLoading} />
      {!!data?.result?.length &&
        data.result.map((item) => (
          <p key={item.codigo}>
            - {item.nome}: {item.descricao}
          </p>
        ))}
      {data?.result?.length === 0 && "(Nenhum item encontrado)"}
    </div>
  );
};

export default ExemploUseEffect;
